/* BookShelf — kitchen-cabinet bookcase view (modelled on the reference photo
 * shared by the user). The look is a warm honey-oak cabinet interior with
 * vertical-panel back wall, brass trim strips above and below the books, soft
 * top-down lighting and books that lean against each other with their front
 * covers slightly visible.
 *
 * Implementation notes:
 *   • Backdrop = warm honey-pine plank pattern + brass trims + inner shadow.
 *   • Each book is a div with a slight tilt; books at the far left lean
 *     hardest (against the cabinet wall), tilt diminishes towards the right.
 *   • Front-cover edge visible — a thin angled "page block" sits to the right
 *     of each spine so the book reads as a real volume, not a flat strip.
 *   • Spines pick a stable per-id cover material (matte / cloth / glossy /
 *     cream-paperback) and a soft pastel palette so they look like modern
 *     cookbooks — Nigella, Ottolenghi, Mary Berry style.
 *   • The Inbox book (system_kind === "inbox") is always first and gets a
 *     distinct amber "INBOX" marker so it's the easiest book to spot.
 */
import { Link } from "react-router-dom";
import { cookbookPath } from "@/lib/slug";
import { Tray } from "@phosphor-icons/react";

// Soft, modern-cookbook palette inspired by the reference photo.
const COVER_PALETTES = [
  { spine: "#e8d8c2", accent: "#7a5a35", text: "#3b2a18" }, // cream
  { spine: "#b9d3b6", accent: "#3b6a3a", text: "#1f3a1f" }, // sage
  { spine: "#f0bfc6", accent: "#a04050", text: "#5a1c28" }, // dusty pink
  { spine: "#2d3a36", accent: "#d4b878", text: "#f4ead2" }, // forest with gold
  { spine: "#c9d8e0", accent: "#3d6878", text: "#1e3c48" }, // misty teal
  { spine: "#f1e7d1", accent: "#b8762b", text: "#5a3a14" }, // butter
  { spine: "#9e6a4f", accent: "#4a2c1a", text: "#f4e6d1" }, // terracotta
  { spine: "#3a5060", accent: "#d4b878", text: "#f4ead2" }, // slate-blue + gold
];

// Material overlays — emulate cloth / linen / paper / matte cover finishes.
const MATERIALS = [
  "repeating-linear-gradient(0deg, rgba(0,0,0,0.05) 0 1px, transparent 1px 4px), repeating-linear-gradient(90deg, rgba(0,0,0,0.04) 0 1px, transparent 1px 4px)", // linen
  "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(0,0,0,0.06) 100%)", // matte
  "repeating-linear-gradient(0deg, rgba(0,0,0,0.06) 0 1px, transparent 1px 3px)", // ribbed cloth
  "linear-gradient(180deg, rgba(255,255,255,0.12) 0 6%, transparent 6% 94%, rgba(0,0,0,0.10) 94%)", // glossy with subtle reflection
];

// Stable per-id hash → deterministic visual props.
function hash(id) {
  let h = 0;
  for (let i = 0; i < (id || "").length; i += 1) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
}

function bookGeometry(id, index, total, isInbox) {
  const h = hash(id);
  // Inbox is always slightly wider so the user can spot it at a glance
  const widthPx = isInbox ? 64 : 44 + ((h >>> 4) % 6) * 6;        // inbox=64, others 44..74
  const heightPct = isInbox ? 100 : 84 + (h % 16);               // inbox stands full-height
  // Books lean LEFT (against the wall on the left). Lean is strongest on the
  // far-left books and falls off towards the right, mirroring the reference
  // photo where one or two books on the right stand upright. The Inbox book
  // itself stands UPRIGHT so its tray icon faces the viewer and reads clearly.
  if (isInbox) return { widthPx, heightPct, leanDeg: 0 };
  const positionFactor = total > 1 ? 1 - (index / (total - 1)) : 0;
  const leanDeg = -((h % 60) / 10) * positionFactor - 1 * positionFactor; // 0° → ~-7°
  return { widthPx, heightPct, leanDeg };
}

function pickPalette(id) {
  const h = hash(id);
  return COVER_PALETTES[(h >>> 8) % COVER_PALETTES.length];
}

function pickMaterial(id) {
  const h = hash(id);
  return MATERIALS[(h >>> 12) % MATERIALS.length];
}

function Spine({ cookbook, index, total }) {
  const cb = cookbook || {};
  const isInbox = cb.is_system && cb.system_kind === "inbox";
  const { widthPx, heightPct, leanDeg } = bookGeometry(cb.cookbook_id || cb.title || "", index, total, isInbox);
  const palette = isInbox
    ? { spine: "#FFAE3D", accent: "#1a0e07", text: "#1a0e07" }     // amber inbox marker — always the brightest
    : pickPalette(cb.cookbook_id || cb.title || "");                // every other book gets a stable diverse pastel — ignores palette_primary so the shelf reads as a real mixed collection
  const material = pickMaterial(cb.cookbook_id || cb.title || "");
  const title = (cb.title || "Untitled").toUpperCase();

  // Curvature shading across the spine width
  const spineBg = [
    material,
    `linear-gradient(90deg, rgba(0,0,0,0.32) 0%, rgba(0,0,0,0.08) 22%, rgba(255,255,255,0.06) 50%, rgba(0,0,0,0.08) 78%, rgba(0,0,0,0.34) 100%)`,
    palette.spine,
  ].join(", ");

  return (
    <Link
      to={cookbookPath(cb)}
      data-testid={`bookshelf-spine-${cb.cookbook_id}`}
      title={cb.title}
      className="group relative inline-block align-bottom origin-bottom transition-transform duration-300 hover:-translate-y-3 hover:rotate-0"
      style={{
        transform: `rotate(${leanDeg}deg)`,
        height: `${heightPct}%`,
        width: `${widthPx}px`,
        filter: "drop-shadow(0 8px 8px rgba(0,0,0,0.45)) drop-shadow(0 1px 1px rgba(0,0,0,0.4))",
      }}
    >
      {/* The book body */}
      <div
        className="relative h-full w-full overflow-hidden flex items-center justify-center"
        style={{
          background: spineBg,
          borderRadius: "1.5px 4px 2px 1.5px",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.22), inset 0 -3px 6px rgba(0,0,0,0.4)",
        }}
      >
        {/* Top edge — exposed paper block (the top of the pages) */}
        <div
          className="absolute top-0 inset-x-0 h-[4px]"
          style={{
            background:
              "linear-gradient(180deg, #faf2d8 0%, #e8d8b0 60%, rgba(0,0,0,0.35) 100%), repeating-linear-gradient(180deg, rgba(0,0,0,0.18) 0 1px, transparent 1px 2px)",
            boxShadow: "inset 0 1px 0 rgba(0,0,0,0.3)",
          }}
          aria-hidden="true"
        />

        {/* Optional accent strip near the bottom — matches cookbook designs */}
        <div
          className="absolute bottom-[14%] inset-x-0 h-[8%]"
          style={{
            background: `linear-gradient(180deg, ${palette.accent}, ${palette.accent})`,
            opacity: 0.85,
          }}
          aria-hidden="true"
        />

        {/* Inbox glyph — a tray icon in the centre for the inbox book */}
        {isInbox ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-1">
            <Tray size={Math.min(widthPx * 0.55, 26)} weight="duotone" className="text-rf-ink mb-1" />
            <p
              className="font-display font-black uppercase text-center leading-[0.95]"
              style={{
                color: palette.text,
                fontSize: widthPx >= 56 ? "10px" : "9px",
                letterSpacing: "0.04em",
                textShadow: "0 1px 0 rgba(255,255,255,0.4)",
              }}
            >
              Inbox
            </p>
          </div>
        ) : (
          // Vertical title
          <p
            className="font-display font-black uppercase tracking-[0.14em] text-center px-1 select-none"
            style={{
              writingMode: "vertical-rl",
              transform: "rotate(180deg)",
              color: palette.text,
              textShadow: "0 1px 0 rgba(0,0,0,0.25), 0 -1px 0 rgba(255,255,255,0.18)",
              fontSize: widthPx >= 56 ? "11px" : "10px",
              lineHeight: 1.05,
              maxHeight: "68%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </p>
        )}

        {/* Right-edge depth shadow — the spine "wraps" round to the page block */}
        <div
          className="absolute right-0 top-0 bottom-0 w-[3px]"
          style={{ background: "linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.45) 100%)" }}
          aria-hidden="true"
        />
        {/* Left-edge highlight */}
        <div
          className="absolute left-0 top-0 bottom-0 w-[1.5px]"
          style={{ background: "linear-gradient(90deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0) 100%)" }}
          aria-hidden="true"
        />
      </div>
    </Link>
  );
}

// Sort: inbox first, the rest in their original order.
function inboxFirst(books) {
  return [...(books || [])].sort((a, b) => {
    const ai = a.is_system && a.system_kind === "inbox" ? 0 : 1;
    const bi = b.is_system && b.system_kind === "inbox" ? 0 : 1;
    return ai - bi;
  });
}

export default function BookShelf({ cookbooks }) {
  const books = inboxFirst(cookbooks);
  if (books.length === 0) {
    return (
      <div className="card-recipe text-center py-12 text-rf-ink-soft" data-testid="dashboard-bookshelf-empty">
        Your shelf is empty — create your first cookbook to start the collection.
      </div>
    );
  }
  return (
    <div data-testid="dashboard-bookshelf">
      <div
        className="relative rounded-md overflow-hidden"
        style={{
          // Outer cabinet frame — warm honey pine, slightly desaturated
          background: "linear-gradient(180deg, #c89968 0%, #a07a4a 100%)",
          boxShadow: "0 14px 36px rgba(0,0,0,0.35)",
        }}
      >
        {/* Inner cabinet cavity */}
        <div
          className="relative m-3"
          style={{
            borderRadius: "3px",
            background: [
              // Vertical board lines on the back wall (every ~14px)
              "repeating-linear-gradient(90deg, rgba(60,32,12,0.22) 0 1px, transparent 1px 14px)",
              // Brass-warm top glow
              "radial-gradient(ellipse 90% 18% at 50% 0%, rgba(255,210,140,0.4), transparent 65%)",
              // Subtle bottom shadow inside the cavity
              "radial-gradient(ellipse 90% 16% at 50% 100%, rgba(40,18,6,0.55), transparent 60%)",
              // Base wood — honey-oak, less orange
              "linear-gradient(180deg, #b9854a 0%, #966a36 50%, #6e4824 100%)",
            ].join(", "),
            boxShadow:
              "inset 0 12px 22px rgba(0,0,0,0.55), inset 0 -8px 18px rgba(0,0,0,0.45), inset 0 0 0 1px rgba(40,20,10,0.35)",
          }}
        >
          {/* Brass top trim with LED-like glow */}
          <div
            className="relative h-[6px]"
            style={{
              background:
                "linear-gradient(180deg, #f0d077 0%, #c89a3f 45%, #8a6420 100%)",
              boxShadow: "0 2px 4px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,240,180,0.6)",
            }}
            aria-hidden="true"
          />
          <div
            className="relative h-[10px]"
            style={{
              background: "linear-gradient(180deg, rgba(255,210,140,0.5), transparent)",
            }}
            aria-hidden="true"
          />

          {/* The books — bottom-aligned in a flex row, wraps when full */}
          <div
            className="relative flex items-end justify-start gap-[1px] flex-wrap px-5 pt-2 pb-1"
            style={{ height: "330px" }}
          >
            {books.map((cb, idx) => (
              <Spine key={cb.cookbook_id} cookbook={cb} index={idx} total={books.length} />
            ))}
          </div>

          {/* Cast-shadow strip immediately under the books */}
          <div
            className="relative h-[6px] -mt-1 pointer-events-none mx-5"
            style={{ background: "radial-gradient(ellipse at center, rgba(0,0,0,0.55), transparent 70%)" }}
            aria-hidden="true"
          />

          {/* Brass bottom trim */}
          <div
            className="relative h-[5px]"
            style={{
              background:
                "linear-gradient(180deg, #f0d077 0%, #c89a3f 50%, #8a6420 100%)",
              boxShadow: "0 -1px 2px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,240,180,0.55)",
            }}
            aria-hidden="true"
          />

          {/* Shelf board underneath — visible honey-wood plank */}
          <div
            className="relative h-[34px]"
            style={{
              background: [
                "repeating-linear-gradient(90deg, rgba(80,40,15,0.16) 0 1px, transparent 1px 9px)",
                "linear-gradient(180deg, #b9803f 0%, #a06a2c 55%, #6e4516 100%)",
              ].join(", "),
              boxShadow: "inset 0 1px 0 rgba(255,220,160,0.4), 0 4px 8px rgba(0,0,0,0.45)",
            }}
            aria-hidden="true"
          />
        </div>
      </div>
      <p className="text-[11px] text-rf-ink-soft mt-3 italic" data-testid="bookshelf-hint">
        Your <b>Inbox</b> cookbook is always first — tap any spine to open it.
      </p>
    </div>
  );
}
